import { useEffect, useState } from "react";
import {
  formatPaymentActivity,
  useFetchPaymentActivityById,
  useFetchPaymentMetadataById,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useAllRecipients,
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useFetchBulkPaymentById } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useFetchBatch } from "byzantine/src/dbbl/businessLogic/entities/wireImport/requests";
import type { PaymentMetadataProps } from "./PaymentActivityDrawer";

type UsePaymentDrawerDataArgs = {
  payment: API.FormattedPaymentActivity | null;
  paymentUuid: API.ScheduledACHPaymentId | API.ACHPaymentId;
};

export type PaymentDrawerData = {
  paymentActivity: API.FormattedPaymentActivity | null;
  paymentMetadata: PaymentMetadataProps | null;
  institutionDetails: { [key: string]: string } | null;
  renameFields: { [key: string]: string };
  correspondentBankName: string | null;
  bulkTitle: string;
  wireImportBatchTitle: string;
  loading: boolean;
};

/**
 * Consolidates all data fetching + derived server state for the payment
 * activity drawer: the payment activity itself, its metadata, associated
 * bulk/wire-import titles, and SWIFT institution / correspondent bank details.
 */
const usePaymentDrawerData = ({
  payment,
  paymentUuid,
}: UsePaymentDrawerDataArgs): PaymentDrawerData => {
  const [paymentActivity, setPaymentActivity] =
    useState<API.FormattedPaymentActivity | null>(payment ?? null);
  const [paymentMetadata, setPaymentMetadata] =
    useState<PaymentMetadataProps | null>(null);
  const [institutionDetails, setInstitutionDetails] = useState<{
    [key: string]: string;
  } | null>(null);
  const [renameFields, setRenameFields] = useState<{
    [key: string]: string;
  }>({});
  const [correspondentBankName, setCorrespondentBankName] = useState<
    string | null
  >(null);
  const [bulkTitle, setBulkTitle] = useState("");
  const [wireImportBatchTitle, setWireImportBatchTitle] = useState("");

  const { send: fetchPaymentActivityById } = useFetchPaymentActivityById();
  const { send: fetchPaymentMetadata, loading } = useFetchPaymentMetadataById();
  const { send: fetchBulkPaymentById } = useFetchBulkPaymentById();
  const { send: fetchWireImportBatchById } = useFetchBatch();
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();

  // if payment activity doesn't exist, fetch it
  useEffect(() => {
    if (!payment && paymentUuid) {
      fetchPaymentActivityById(paymentUuid)
        .then((response) => {
          const formattedData = formatPaymentActivity(
            response as API.PaymentActivity,
            recipients,
            accounts,
          );
          setPaymentActivity(formattedData);
        })
        .catch((e) => console.error("Error fetching payment activity:", e));
    }
  }, [payment, paymentUuid, recipients, accounts]);

  useEffect(() => {
    if (payment) {
      setPaymentActivity(payment);
    }
  }, [payment]);

  useEffect(() => {
    const fetchSinglePaymentMetadata = async () => {
      // a reversal's company/SEC/entry details live on the original ACH
      // payment, so fetch the original payment's metadata (via the reversal's
      // `originalPayment` uuid)
      if (paymentActivity?.isReversal) {
        if (!paymentActivity?.originalPayment) return;
        try {
          const data = await fetchPaymentMetadata(
            paymentActivity.originalPayment,
            "UnverifiedACHTransaction",
          );
          setPaymentMetadata(data ?? null);
        } catch (error) {
          setPaymentMetadata(null);
        }
        return;
      }
      if (paymentActivity?.uuid && paymentActivity?.model) {
        try {
          const data = await fetchPaymentMetadata(
            paymentActivity?.uuid,
            paymentActivity?.model,
          );
          setPaymentMetadata(data ?? null);
        } catch (error) {
          setPaymentMetadata(null);
        }
      }
    };

    const fetchBulkPaymentDetails = async () => {
      if (paymentActivity?.bulkPaymentId) {
        try {
          const data = await fetchBulkPaymentById(
            paymentActivity.bulkPaymentId,
          );
          setBulkTitle(data?.title ?? "");
        } catch (error) {
          setBulkTitle("");
        }
      }
    };

    const fetchWireImportBatchDetails = async () => {
      if (paymentActivity?.wireImportBatchId) {
        try {
          const data = await fetchWireImportBatchById(
            paymentActivity.wireImportBatchId,
          );
          setWireImportBatchTitle(data?.description ?? "");
        } catch (error) {
          setWireImportBatchTitle("");
        }
      }
    };

    setBulkTitle("");
    setWireImportBatchTitle("");
    fetchSinglePaymentMetadata();
    fetchBulkPaymentDetails();
    fetchWireImportBatchDetails();
  }, [paymentActivity]);

  useEffect(() => {
    // reset before (re)fetching so a non-international payment, or one without
    // SWIFT details, doesn't show stale labels from a previously viewed wire
    setInstitutionDetails(null);
    setRenameFields({});

    const swiftBic =
      paymentActivity?.destinationDetails?.bic ||
      paymentActivity?.recipient?.swift_destination?.bic;

    if (!paymentActivity?.isInternationalWirePayment || !swiftBic) {
      return;
    }

    const fetchAndSetAdditionalDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.additional_fields) {
        setInstitutionDetails({
          institutionName: res.institution_name,
        });
        setRenameFields(res.rename_fields);
      }
    };

    fetchAndSetAdditionalDetails(swiftBic).catch(() => {
      setInstitutionDetails(null);
      setRenameFields({});
    });
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
  ]);

  // Fetch correspondent bank name and country-based rename fields
  useEffect(() => {
    // reset before (re)fetching so navigating away from a wire with a
    // correspondent bank doesn't leave the previous name visible
    setCorrespondentBankName(null);

    const correspondentBic =
      paymentActivity?.recipient?.swift_destination?.correspondent_bank_bic;

    if (!paymentActivity?.isInternationalWirePayment || !correspondentBic) {
      return;
    }

    const fetchCorrespondentBankDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.institution_name) {
        setCorrespondentBankName(res.institution_name);
      }
      const countryCode =
        paymentActivity?.recipient?.swift_destination?.branch_address
          ?.country_code;
      if (countryCode) {
        const countryDetails =
          await fetchSwiftAdditionalFieldsByCountry(countryCode);
        if (countryDetails?.rename_fields) {
          setRenameFields(countryDetails.rename_fields);
        }
      }
    };

    fetchCorrespondentBankDetails(correspondentBic).catch(() => {
      setCorrespondentBankName(null);
    });
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
    fetchSwiftAdditionalFieldsByCountry,
  ]);

  return {
    paymentActivity,
    paymentMetadata,
    institutionDetails,
    renameFields,
    correspondentBankName,
    bulkTitle,
    wireImportBatchTitle,
    loading,
  };
};

export default usePaymentDrawerData;
